import { ROUTES } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import { transactionStatus } from 'Models/Enums';
import { store } from 'Models/Store';
import React from 'react';
import { Link } from 'react-router-dom';
import { TableRow, TableCell, Checkbox } from 'semantic-ui-react';
import { downloadInvoice } from 'Services/Api/_HumanWritten/InvoiceService';
import { getMessageByKey } from 'Stores/LanguageStore';
import { formatStandardDateTime } from 'Util/_HumanWritten/DateUtils';
import { anytimeTicketLabel } from 'Util/_HumanWritten/anytimeTicketTerm';
import { isNotNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { CustomTooltipWithCustomTrigger } from 'Views/Components/_HumanWritten/CustomTooltip/CustomToolTipWithCustomTrigger';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import { ExpandableTableCell } from 'Views/Components/_HumanWritten/StaffTables/ExpandableTableCell';
import { useQueryClient } from '@tanstack/react-query';
import { showChangeFeesModal } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/Modals/ChangeFeesModal';
import {
	useStaffTransactionsStore,
	transactionListStatus,
} from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/StaffTransactionsFilterStore';
import { TransactionColumnKeys } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionColumnKeys';
import { TransactionListSummaryDto } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionsTableComponent';
import { TransactionStatusTag } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionStatusTag';
import If from 'Views/Components/If/If';

interface TransactionRenderProps {
	transaction: TransactionListSummaryDto;
	isChecked: boolean;
	onToggleCheckbox: () => void;
	onViewDetails?: (transactionId: string) => void;
	isColumnVisible?: (key: string) => boolean;
}

const getStatusBadgeClass = (status: string) => {
	switch (status) {
		case 'COMPLETED':
		case 'EFTPOS':
		case 'CASH':
			return 'status-badge--paid';
		case 'IN_PROGRESS':
			return 'In progress';
		case 'INVOICED':
			return 'status-badge--invoiced';
		case 'PENDING_INVOICE':
			return 'status-badge--awaiting';
		case 'FAILED':
			return 'status-badge--failed';
		case 'EXPIRED':
		case 'ABANDONED':
			return 'Abandoned';
		case 'PENDING':
			return 'Pending';
		case 'PENDING_PAYMENT':
			return 'Unpaid';
		default:
			return status;
	}
};

/**
 * The Type cell's text. The server sends a fixed category name (it is built inside the EF projection,
 * where no language lookup can run); the anytime-ticket one is shown in this operator's own term
 * (#2685), the rest as sent.
 */
function entityTypeLabel(entityType: string | null | undefined): string {
	if (entityType === 'Anytime Ticket') {
		return anytimeTicketLabel();
	}
	return entityType ?? '-';
}

export function TransactionRender({
	transaction,
	isChecked,
	onToggleCheckbox,
	isColumnVisible,
}: TransactionRenderProps) {
	const [showModal, setShowModal] = React.useState(false);
	const { setSearchString } = useStaffTransactionsStore();
	const queryClient = useQueryClient();

	const isExpired = transaction.expirationTime && new Date(transaction.expirationTime) < new Date();
	const showColumn = (key: string) => !isColumnVisible || isColumnVisible(key);

	// Only surface the "Paid" subtext when it adds information: skip $0 transactions, and skip when the
	// payment time is the same as the created time (the common pay-at-booking case), so it isn't just a
	// duplicate of the created date.
	const createdDateFormatted = formatStandardDateTime(transaction.createdDate);
	const paidDateFormatted = isNotNullOrUndefined(transaction.paidAt)
		? formatStandardDateTime(transaction.paidAt)
		: undefined;
	const showPaidDate =
		isNotNullOrUndefined(paidDateFormatted) &&
		transaction.totalAmount != null &&
		transaction.totalAmount !== 0 &&
		paidDateFormatted !== createdDateFormatted;

	return (
		<TableRow className={isExpired ? 'expired-transaction' : ''}>
			{/* Matches the conditional checkbox column in TransactionsTableComponent — selection is
			 * manager-only, so the cell must disappear with the header or the row shifts. */}
			{store.isManager && (
				<TableCell
					onClick={(e: any) => {
						e.stopPropagation();
						onToggleCheckbox();
					}}
				>
					<Checkbox checked={isChecked} onChange={onToggleCheckbox} onClick={e => e.stopPropagation()} />
				</TableCell>
			)}
			<TableCell className="minimum">
				{transaction.inCheckIn ? (
					<CustomTooltipWithCustomTrigger
						trigger={<Icon name="issue" />}
						content="This transaction was processed in check-in."
					/>
				) : undefined}
			</TableCell>
			<TableCell>#{transaction.humanReadableId}</TableCell>
			{showColumn(TransactionColumnKeys.CREATED_DATE) && (
				<TableCell>
					{createdDateFormatted}
					{showPaidDate && <p className="table-cell-subtext">Paid {paidDateFormatted}</p>}
				</TableCell>
			)}
			{showColumn(TransactionColumnKeys.CUSTOMER) && (
				<TableCell>
					<Link
						to={ROUTES.USER_DETAIL.url(transaction.userId, 'transactions')}
						className="table-cell__container hide-underline"
					>
						<div className="customer-details">
							<div className="customer-details__inner">
								<p>
									{transaction.name}{' '}
									<span className="user-display-name">
										{isNotNullOrUndefined(transaction.userDisplayName) &&
											`- ${transaction.userDisplayName}`}
									</span>
								</p>
								<p>{transaction.email}</p>
							</div>
						</div>
					</Link>
				</TableCell>
			)}
			{/* eslint-disable no-nested-ternary */}
			{showColumn(TransactionColumnKeys.BOOKINGS) &&
				(() => {
					// Link by booking id, not by reference: two bookings can share a reference, and
					// /bookings/<reference> resolves with a first-match lookup that opens an arbitrary
					// one of them. bookingLinks carries both; fall back to the reference-only list so a
					// response from before that field existed still renders.
					const bookingItems =
						transaction.bookingLinks && transaction.bookingLinks.length > 0
							? Array.from(new Map(transaction.bookingLinks.map(b => [b.id, b])).values())
									.sort((a, b) => a.humanReadableId.localeCompare(b.humanReadableId))
									.map(b => `booking:${b.id}:${b.humanReadableId}`)
							: (transaction.bookingIds ?? []).sort((a: any, b: any) => a.localeCompare(b));

					const referenceItems = [
						...bookingItems,
						...(transaction.refundVoucherCodes ?? []).map((code: any) => `voucher:${code}`),
						// Vouchers spent on the transaction — a credit-funded parking or anytime purchase
						// has these and no refund codes, so it would otherwise show no voucher reference.
						...(transaction.usedVoucherCodes ?? []).map((code: any) => `voucher:${code}`),
						...(transaction.purchasedVoucherCode ? [`voucher:${transaction.purchasedVoucherCode}`] : []),
						...(transaction.purchasedEntitlementCode
							? [`entitlement:${transaction.purchasedEntitlementCode}`]
							: []),
						...(transaction.purchasedItemId ? [`pos:${transaction.purchasedItemId}`] : []),
						...(transaction.parkingReservationIds ?? []).map(id => `parking:${id}`),
						// Ahead of the refunds, so a refunded charter reads as the charter and then what
						// was given back on it.
						// Id first, then the label: a GUID never holds a colon, so the two come apart on
						// the first one whatever the label turns out to be.
						//
						// Keyed off the charter's ID rather than its NAME. A charter can be saved without
						// a name, and `charterName` is then null — so the whole reference was dropped and
						// a charter booked without one had no reference in this cell at all.
						//
						// The label is the charter's own reference, which is unique and is what every
						// other reference in this cell is. The name stood in for it and could not do the
						// job: two charters called "Waste run" read identically. It is still the fallback
						// for a charter taken before charters had a reference, and the plain word for one
						// that has neither.
						...(isNotNullOrUndefined(transaction.charterId)
							? [
									`charter:${transaction.charterId}:${
										transaction.charterHumanReadableId || transaction.charterName || 'Charter'
									}`,
								]
							: []),
						...(transaction.refundIds ?? []).map(id => `refund:${id}`),
					];

					if (referenceItems.length === 0) {
						return <TableCell>-</TableCell>;
					}

					return (
						<ExpandableTableCell
							content={referenceItems}
							maxItems={2}
							renderItem={(item: any) => {
								if (item.startsWith('booking:')) {
									// "booking:<guid>:<reference>". Split on the first colon after the
									// prefix — a GUID never contains one, and the reference is A-Z0-9.
									const rest = item.slice('booking:'.length);
									const separator = rest.indexOf(':');
									const bookingGuid = rest.slice(0, separator);
									const reference = rest.slice(separator + 1);
									return (
										<Link
											key={`b-${bookingGuid}`}
											to={`/bookings/${bookingGuid}`}
											className="table-cell__container hide-underline reference-link"
										>
											<Icon name="confirmation_number" useMaterialIcons />#{reference}
										</Link>
									);
								}
								if (item.startsWith('voucher:')) {
									const code = item.replace('voucher:', '');
									return (
										<Link
											key={`v-${code}`}
											to={`/vouchers?search=${encodeURIComponent(code)}`}
											className="table-cell__container hide-underline voucher-id reference-link"
										>
											<Icon name="payments" useMaterialIcons />#{code}
										</Link>
									);
								}
								if (item.startsWith('entitlement:')) {
									const code = item.replace('entitlement:', '');
									return (
										<Link
											key={`e-${code}`}
											to={`/anytime-tickets?search=${encodeURIComponent(code)}`}
											className="table-cell__container hide-underline reference-link"
										>
											<Icon name="local_activity" useMaterialIcons />#{code}
										</Link>
									);
								}
								if (item.startsWith('pos:')) {
									const id = item.replace('pos:', '');
									return (
										<div key="pos" className="table-cell__container reference-link">
											<Icon name="shopping_cart" useMaterialIcons />#{id}
										</div>
									);
								}
								if (item.startsWith('parking:')) {
									const id = item.replace('parking:', '');
									return (
										<Link
											key={`p-${id}`}
											to={`/parking-reservations/${id}`}
											className="table-cell__container hide-underline reference-link"
										>
											<Icon name="local_parking" useMaterialIcons />#{id}
										</Link>
									);
								}
								if (item.startsWith('charter:')) {
									const rest = item.replace('charter:', '');
									const separator = rest.indexOf(':');
									const id = rest.slice(0, separator);
									const name = rest.slice(separator + 1);
									// The customer's view of the charter, which is what the charter
									// sidebar opens too. Every charter with a transaction is booked, or
									// booked and later called off, and that view reads both.
									return (
										<Link
											key={`c-${id}`}
											to={ROUTES.CUSTOMER_CHARTER.url(id)}
											className="table-cell__container hide-underline reference-link"
										>
											<Icon name="directions_boat" useMaterialIcons />
											{name}
										</Link>
									);
								}
								if (item.startsWith('refund:')) {
									const id = item.replace('refund:', '');
									// Show a short prefix of the refund GUID — the search param carries
									// the full id so the Refunds table can match exactly.
									const short = id.split('-')[0].toUpperCase();
									return (
										<Link
											key={`r-${id}`}
											to={`/refunds?search=${encodeURIComponent(id)}`}
											className="table-cell__container hide-underline reference-link"
										>
											<Icon name="currency_exchange" useMaterialIcons />#{short}
										</Link>
									);
								}
								return (
									<Link
										key={item}
										to={`/bookings/${item}`}
										className="table-cell__container hide-underline reference-link"
									>
										<Icon name="confirmation_number" useMaterialIcons />#{item}
									</Link>
								);
							}}
						/>
					);
				})()}
			{/* eslint-enable no-nested-ternary */}
			{showColumn(TransactionColumnKeys.ENTITY_TYPE) && (
				<TableCell>{entityTypeLabel(transaction.entityType)}</TableCell>
			)}
			{showColumn(TransactionColumnKeys.TYPE) && <TableCell>{transaction.typeOfTransaction}</TableCell>}
			{showColumn(TransactionColumnKeys.AMOUNT) && (
				<TableCell textAlign="right">
					{isNotNullOrUndefined(transaction.totalAmount) && isNotNullOrUndefined(transaction.paidAmount)
						? `${transaction.paidAmount.toFixed(2)}/${transaction.totalAmount.toFixed(2)}`
						: '-'}
				</TableCell>
			)}
			{showColumn(TransactionColumnKeys.STATUS) && (
				<TableCell>
					<span
						className={`status-badge ${getStatusBadgeClass(transaction.status)}`}
						data-test-id="transaction-status-badge"
					>
						<TransactionStatusTag
							status={
								(transaction.isTerminalPayment
									? 'POS_PAYMENT'
									: transaction.status) as transactionListStatus
							}
						/>
					</span>
				</TableCell>
			)}
			{showColumn(TransactionColumnKeys.CREATED_BY) && (
				<TableCell>
					{transaction.createdByType === 'Staff' && transaction.createdByName
						? `Staff - ${transaction.createdByName}`
						: transaction.createdByType || '-'}
				</TableCell>
			)}
			<TableCell collapsing textAlign="center">
				{/*
				 * The actions cell used to be hidden whenever `purchasedVoucherCode` was set, which took
				 * the tax-invoice download with it — so a voucher purchase had no way to produce an
				 * invoice from this table at all, even though the same customer's Invoices tab offered
				 * one and the endpoint supports it. Each item in the menu carries its own condition
				 * (`Change fees` is gated on `canEditFees`, which a voucher purchase never has), so the
				 * cell only needs to know whether the transaction is a live one.
				 */}
				<If
					condition={
						(transaction.status !== 'ABANDONED' &&
							transaction.status !== 'FAILED' &&
							transaction.status !== 'IN_PROGRESS') ||
						(appConfigStore.allowedRefundMethodsFor(store.isStaff).has('Credit') && transaction.canEditFees)
					}
				>
					<Popover
						popoverClassName="fh-popover__content-list"
						align="right"
						buttonContent={<Icon name="more-vertical" />}
						popoverContent={onClose => (
							<div className="options">
								<If
									condition={
										transaction.status !== 'ABANDONED' &&
										transaction.status !== 'FAILED' &&
										transaction.typeOfTransaction !== 'Cancelled'
									}
								>
									<button
										className="fh-popover__list-item"
										onClick={() => {
											// The endpoint branches on these flags, so each purchase type has
											// to be named. `isVoucherPurchase` was hardcoded false and
											// `isAnytimeTicket` was never passed, so an entitlement purchase
											// fell through to the booking-invoice branch and a voucher
											// purchase could not be reached at all.
											const isVoucherPurchase = !stringIsEmpty(transaction.purchasedVoucherCode);
											const isAnytimeTicket = !stringIsEmpty(
												transaction.purchasedEntitlementCode,
											);
											const isParkingOnly =
												!transaction.bookingIds?.length &&
												!isVoucherPurchase &&
												!isAnytimeTicket &&
												!!transaction.parkingReservationIds?.length;
											downloadInvoice(
												transaction.id,
												transaction.isEventBooking,
												isVoucherPurchase,
												isParkingOnly,
												isAnytimeTicket,
											);
											onClose();
										}}
									>
										{getMessageByKey('tax-invoice__download__button', 'Download tax invoice')}
									</button>
								</If>
								{/*
								 * Fees can be corrected on a cancellation (re-splitting the refund pool
								 * between the fee kept and the credit given back) and on an alteration
								 * (the change fee plus any trailer-removal fee, with the difference
								 * credited back). canEditFees is the server's own answer, so the button
								 * can't offer an edit the endpoint will refuse.
								 */}
								<If
									condition={
										store.isManager &&
										appConfigStore.allowedRefundMethodsFor(store.isStaff).has('Credit') &&
										transaction.canEditFees
									}
								>
									<button
										className="fh-popover__list-item"
										onClick={() => {
											showChangeFeesModal(transaction.id, () => {
												// Refetch the list. This used to rely on setSearchString
												// alone: changing the search term happens to change the
												// query key, which refetches. That works exactly once —
												// edit the same transaction again and the search is already
												// its reference, so the state never changes, the key never
												// changes, and the row keeps showing the old fee.
												//
												// Invalidate by key prefix rather than calling the list's
												// own refetch, so this works whatever filters or page the
												// staff member is on without threading a callback down.
												void queryClient.invalidateQueries({
													queryKey: ['staffTransactions'],
												});
												// Still narrow to the edited transaction, so the row it
												// affected is the one in front of them.
												setSearchString(transaction.humanReadableId);
											}).catch(() => {});
											onClose();
										}}
									>
										Change fees
									</button>
								</If>
								{/* Add more action buttons here as needed */}
							</div>
						)}
					/>
				</If>
			</TableCell>
		</TableRow>
	);
}
